// This quantity contract is shared by capture, review, persistence and display.
const clean = (value) => String(value ?? "").trim();
const AMOUNT = String.raw`(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)?`;
const UNIT = String.raw`(?:mcg|[µμ]g|mg|kg|g|mL|L|IU|units?|tablets?|capsules?|puffs?|drops?|patch(?:es)?|sprays?|mEq|mmol|%)(?:\s*\/\s*(?:\d+(?:\.\d+)?\s*)?(?:mL|L))?`;
const WHOLE = new RegExp(`^(${AMOUNT})\\s*(${UNIT})$`, "i");
const NUMBER = new RegExp(`^${AMOUNT}$`);
const escape = (value) => clean(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const normalizeUnit = (value) =>
  clean(value)
    .replace(/\s/g, "")
    .replace(/ml/gi, "mL")
    .replace(/^iu$/i, "IU")
    .replace(/^[µμ]g$/i, "mcg");
const unitKey = (value) => normalizeUnit(value).toLowerCase();
const label = (amount, unit) =>
  `${amount}${/^(?:units?|tablets?|capsules?|puffs?|drops?|patch(?:es)?|sprays?)$/i.test(unit) ? " " : ""}${unit}`;
const readMedicationDose = ({
  dose = "",
  unit = "",
  doseText = "",
  medicationName = "",
  sourceText = "",
} = {}) => {
  const sourceMentionMatched = Boolean(
    medicationName &&
    sourceText &&
    new RegExp(`\\b${escape(medicationName)}\\s*:?\\s+${AMOUNT}`, "i").test(
      sourceText,
    ),
  );
  const raw = clean(dose || doseText),
    suppliedUnit = normalizeUnit(unit);
  const parsed = raw.match(WHOLE);
  let amount = parsed
    ? parsed[1].replace(/\s/g, "")
    : NUMBER.test(raw)
      ? raw.replace(/\s/g, "")
      : "";
  let resolvedUnit = parsed ? normalizeUnit(parsed[2]) : suppliedUnit;
  let reason = "";
  if (parsed && suppliedUnit && unitKey(suppliedUnit) !== unitKey(parsed[2]))
    reason = "Dose and unit conflict. Confirm both.";
  if (medicationName && sourceText) {
    const pattern = new RegExp(
      `\\b${escape(medicationName)}\\s*:?\\s+(${AMOUNT})\\s*(${UNIT})(?![a-zA-Zµμ])`,
      "gi",
    );
    const mentions = [...clean(sourceText).matchAll(pattern)].map((match) => ({
      amount: match[1].replace(/\s/g, ""),
      unit: normalizeUnit(match[2]),
    }));
    const matches = mentions.filter(
      (item) => !amount || item.amount === amount,
    );
    const unique = [
      ...new Map(
        matches.map((item) => [label(item.amount, item.unit), item]),
      ).values(),
    ];
    if (unique.length === 1) {
      if (resolvedUnit && unitKey(resolvedUnit) !== unitKey(unique[0].unit))
        reason = "The source gives a different dose unit. Confirm both.";
      else {
        amount = unique[0].amount;
        resolvedUnit = unique[0].unit;
      }
    } else if (mentions.length && amount && !unique.length)
      reason =
        "The source gives a different dose. Confirm the amount and unit.";
    else if (unique.length > 1)
      reason =
        "The source has more than one dose. Confirm the intended dose and unit.";
  }
  if (
    !amount ||
    !resolvedUnit ||
    !new RegExp(`^${UNIT}$`, "i").test(resolvedUnit)
  )
    reason = reason || "Dose and unit need clarification.";
  const valid = !reason;
  return {
    amount: valid ? amount : "",
    unit: valid ? resolvedUnit : "",
    doseText: valid ? label(amount, resolvedUnit) : "",
    needsClarification: !valid,
    reason,
    sourceMentionMatched,
  };
};
const formatMedicationDose = (value) => {
  const quantity = readMedicationDose(value);
  return (
    quantity.doseText ||
    (clean(value?.dose || value?.doseText) || clean(value?.unit)
      ? "Dose and unit need clarification"
      : "")
  );
};
const withMedicationDose = (record) => {
  const quantity = readMedicationDose({
    dose: record.dose,
    unit: record.unit,
    doseText: record.doseText,
    medicationName: record.name || record.medicationName,
    sourceText: record.sourceText,
  });
  return {
    ...record,
    dose: quantity.amount,
    unit: quantity.unit,
    doseText: quantity.doseText,
    ...(quantity.needsClarification
      ? {
          needsReview: true,
          doseNeedsClarification: true,
          reviewReasons: [
            ...new Set([...(record.reviewReasons || []), "dose_unit_needed"]),
          ],
        }
      : { doseNeedsClarification: false }),
  };
};
const formatMedicationFactText = ({ text = "", name = "", quantity }) => {
  if (!name) return text;
  const pattern = new RegExp(
    `\\b${escape(name)}(?:\\s+(${AMOUNT})\\s*(${UNIT})?)?\\b`,
    "gi",
  );
  return String(text).replace(pattern, (match, amount, unit) => {
    if (!quantity.doseText) return `${name} (dose and unit need clarification)`;
    if (amount && amount.replace(/\s/g, "") !== quantity.amount)
      return unit ? match : `${name} (dose and unit need clarification)`;
    return `${name} ${quantity.doseText}`;
  });
};
const formatMedicationPlanDose = (record) => {
  const schedules = Array.isArray(record.schedules) ? record.schedules : [];
  if (!schedules.length)
    return formatMedicationDose({
      dose: record.dose || record.strength,
      unit: record.unit,
      doseText: record.doseText,
    });
  return [
    ...new Set(
      schedules.map((schedule) =>
        [
          formatMedicationDose({
            dose: schedule.dose || record.dose,
            unit: schedule.unit || record.unit,
            doseText: schedule.doseText,
          }),
          clean(schedule.time),
        ]
          .filter(Boolean)
          .join(" at "),
      ),
    ),
  ]
    .filter(Boolean)
    .join("; ");
};
const preserveMedicationDoseText = (text, medications = []) => {
  let output = String(text || "");
  const groups = new Map();
  medications.forEach((medication) => {
    const name = clean(medication.name || medication.medicationName);
    if (!name) return;
    const key = name.toLowerCase();
    if (!groups.has(key)) groups.set(key, { name, values: [] });
    groups.get(key).values.push(readMedicationDose(medication));
  });
  const protect = (name, values, requireAmount = false) => {
    const pattern = new RegExp(
      `\\b${escape(name)}(?:\\s+(${AMOUNT})\\s*(${UNIT})?)?\\b`,
      "gi",
    );
    output = output.replace(pattern, (match, amount, unit) => {
      if (requireAmount && !amount) return match;
      const eligible = values.filter(
        (value) =>
          value.doseText &&
          (!amount || value.amount === amount.replace(/\s/g, "")) &&
          (!unit || unitKey(value.unit) === unitKey(unit)),
      );
      const unique = [
        ...new Map(eligible.map((value) => [value.doseText, value])).values(),
      ];
      if (unique.length === 1) return `${name} ${unique[0].doseText}`;
      if (!amount && unique.length > 1) return match;
      return `${name} (dose and unit need clarification)`;
    });
  };
  groups.forEach(({ name, values }) => protect(name, values));
  if (groups.size === 1) protect("dose", [...groups.values()][0].values, true);
  return output;
};
const medicationQuantities = (record) => {
  const base = record.canonicalMedicationRecord?.medication || record;
  const schedules = record.schedules?.length ? record.schedules : [base];
  return schedules.map((schedule) => {
    const dose = clean(schedule.dose || base.dose),
      unit = clean(schedule.unit || base.unit);
    const quantity = readMedicationDose({
      dose,
      unit,
      doseText: schedule.doseText || base.doseText,
    });
    return {
      name: clean(base.name || record.name || record.medicationName),
      dose,
      unit,
      doseText: quantity.doseText,
      doseNeedsClarification: quantity.needsClarification,
    };
  });
};
const prepareMedicationDoseRecord = (record) => {
  const quantities = medicationQuantities(record);
  const resolved = quantities.map((value) => {
    const quantity = readMedicationDose(value);
    if ((value.dose || value.doseText) && quantity.needsClarification)
      throw new Error(quantity.reason);
    return quantity;
  });
  const first = resolved[0] || readMedicationDose(record);
  const fields = {
    dose: first.amount,
    unit: first.unit,
    doseText: first.doseText,
    doseNeedsClarification: first.needsClarification,
  };
  return {
    ...record,
    ...fields,
    reviewReasons: (record.reviewReasons || []).filter(
      (reason) =>
        !first.doseText ||
        !["missing_dose", "dose_unit_needed"].includes(reason),
    ),
    ...(record.schedules?.length
      ? {
          schedules: record.schedules.map((schedule, index) => ({
            ...schedule,
            dose: resolved[index].amount,
            unit: resolved[index].unit,
            doseText: resolved[index].doseText,
          })),
        }
      : {}),
    ...(record.canonicalMedicationRecord
      ? {
          canonicalMedicationRecord: {
            ...record.canonicalMedicationRecord,
            medication: {
              ...record.canonicalMedicationRecord.medication,
              ...fields,
            },
          },
        }
      : {}),
    ...(first.needsClarification ? { needsReview: true } : {}),
  };
};
export {
  readMedicationDose,
  formatMedicationDose,
  withMedicationDose,
  formatMedicationFactText,
  formatMedicationPlanDose,
  preserveMedicationDoseText,
  medicationQuantities,
  prepareMedicationDoseRecord,
};
